import {
  IconBookmark,
  SequenceMenu,
  TextFieldInput,
  SequenceMenuItemProps,
  useNotifications,
  GreyGrey20,
  GreyGrey70,
  GreyGrey90,
  Primary13SemiBold,
  Box,
  Grid,
  GridItem,
  Link
} from '@sequencehq/core-components'
import { NavSection } from 'components/NavigationV3/components/NavSection'
import { useLocation } from 'react-router-dom'
import { useMutation, useQuery } from '@sequencehq/api'
import { dashboard20240730Client } from '@sequencehq/api/dashboard/v20240730'
import { NavLinksLoading } from 'components/NavigationV3/components/NavLinks/NavLinksLoading'
import { savedViewsPorts } from 'SavedViews/savedViews.ports'
import {
  SAVED_VIEWS_STALE_TIME,
  SAVED_VIEWS_ORDER_KEY
} from 'SavedViews/savedViews.constants'
import { FC, KeyboardEvent, useMemo, useState } from 'react'
import { DeleteSavedViewDialog } from './DeleteSavedViewDialog'
import { SavedView } from '../savedViews.types'
import { NavTooltip } from 'components/NavigationV3/components/NavTooltip'
import { useIsMobile } from 'hooks/useIsMobile'
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
  DragStartEvent
} from '@dnd-kit/core'
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
  arrayMove
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { restrictToVerticalAxis } from '@dnd-kit/modifiers'

const getSavedViewsOrder = (): string[] => {
  try {
    const stored = localStorage.getItem(SAVED_VIEWS_ORDER_KEY)
    return stored ? JSON.parse(stored) : []
  } catch {
    return []
  }
}

const setSavedViewsOrder = (ids: string[]): void => {
  try {
    localStorage.setItem(SAVED_VIEWS_ORDER_KEY, JSON.stringify(ids))
  } catch {
    // Ignore localStorage errors
  }
}

const sortViewsByOrder = (
  views: SavedView[],
  orderIds: string[]
): SavedView[] => {
  const orderMap = new Map(orderIds.map((id, index) => [id, index]))
  return [...views].sort((a, b) => {
    const aIndex = orderMap.get(a.id)
    const bIndex = orderMap.get(b.id)
    // Views not in order go to end
    if (aIndex === undefined && bIndex === undefined) return 0
    if (aIndex === undefined) return 1
    if (bIndex === undefined) return -1
    return aIndex - bIndex
  })
}

interface EditableSavedViewProps {
  view: SavedView
  isEditing: boolean
  isHovered: boolean
  isActive: boolean
  editingName: string
  onEditingNameChange: (name: string) => void
  onStartEdit: () => void
  onSave: () => void
  onCancel: () => void
  onMouseEnter: () => void
  onMouseLeave: () => void
  isCollapsed?: boolean
  isDragActive?: boolean
}

const EditableSavedView: FC<EditableSavedViewProps> = ({
  view,
  isEditing,
  isHovered,
  isActive,
  editingName,
  onEditingNameChange,
  onStartEdit,
  onSave,
  onCancel,
  onMouseEnter,
  onMouseLeave,
  isCollapsed = false,
  isDragActive = false
}) => {
  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      void onSave()
    } else if (e.key === 'Escape') {
      onCancel()
    }
  }

  const getKebabMenuItems = () => [
    (props: Partial<SequenceMenuItemProps>) => (
      <SequenceMenu.Item
        key="edit-name"
        uuid="edit-name"
        label="Edit name"
        onClick={() => {
          onStartEdit()
          props.setIsOpen?.(false)
        }}
      />
    ),
    (props: Partial<SequenceMenuItemProps>) => (
      <DeleteSavedViewDialog
        key="delete-saved-view"
        savedViewId={view.id}
        onCancel={() => props.setIsOpen?.(false)}
      />
    )
  ]

  const navLink = (
    <Box
      position="relative"
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      {isEditing ? (
        <TextFieldInput
          value={editingName}
          onChange={e => onEditingNameChange(e.target.value)}
          autoFocus
          onKeyDown={handleKeyDown}
          onBlur={onSave}
          maxLength={64}
          placeholder="Name"
        />
      ) : (
        <Box position="relative">
          <Grid
            as={Link.Unstyled}
            to={`saved-view/${view.id}`}
            data-testid={`savedViews.${view.id}`}
            padding="0 8px"
            templateColumns={isCollapsed ? 'auto' : 'auto 1fr auto'}
            height="32px"
            gap="8px"
            borderRadius="6px"
            alignContent="center"
            alignItems="center"
            justifyContent={'flex-start'}
            cursor="pointer"
            color={isActive ? GreyGrey90 : GreyGrey70}
            backgroundColor={isActive ? GreyGrey20 : 'transparent'}
            style={{
              willChange: 'width, transform',
              transform: 'translateZ(0)',
              transition: 'background-color 0.2s ease',
              pointerEvents: isDragActive ? 'none' : 'auto'
            }}
            _hover={{
              backgroundColor: GreyGrey20
            }}
          >
            <GridItem
              height="fit-content"
              display="flex"
              alignContent="center"
              justifyContent={'flex-start'}
              flexShrink={0}
              alignSelf="center"
              style={{
                willChange: 'transform',
                transform: 'translateZ(0)',
                transition: 'transform 0.3s ease'
              }}
            >
              <IconBookmark color="inherit" />
            </GridItem>
            {!isCollapsed && (
              <GridItem
                {...Primary13SemiBold}
                color="inherit"
                overflow="hidden"
                textOverflow="ellipsis"
                className="nav-text"
              >
                {view.name}
              </GridItem>
            )}
          </Grid>
          {isHovered && !isCollapsed && (
            <Box
              position="absolute"
              right="8px"
              top="50%"
              transform="translateY(-50%)"
              zIndex={1}
            >
              <SequenceMenu
                variant="ghost"
                buttonSize="small"
                items={getKebabMenuItems()}
              />
            </Box>
          )}
        </Box>
      )}
    </Box>
  )

  if (isCollapsed) {
    return (
      <NavTooltip label={view.name} isDisabled={!isCollapsed}>
        {navLink}
      </NavTooltip>
    )
  }

  return navLink
}

interface SortableSavedViewProps
  extends Omit<EditableSavedViewProps, 'isDragActive'> {
  id: string
  isDragActive: boolean
}

const SortableSavedView: FC<SortableSavedViewProps> = ({
  id,
  isDragActive,
  ...props
}) => {
  const { attributes, listeners, setNodeRef, transform, transition, active } =
    useSortable({ id })

  const isActivelyDragging = !!active && active.id === id

  const style = {
    transform: CSS.Translate.toString(transform),
    transition,
    ...(isActivelyDragging ? { position: 'relative' as const, zIndex: 1 } : {})
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      data-testid={`savedViews.sortable.${id}`}
      {...attributes}
      {...listeners}
    >
      <EditableSavedView {...props} isDragActive={isDragActive} />
    </div>
  )
}

interface SavedViewsNavigationProps {
  isCollapsed?: boolean
}

export const SavedViewsNavigation: FC<SavedViewsNavigationProps> = ({
  isCollapsed = false
}) => {
  const { pathname } = useLocation()
  const { displayNotification } = useNotifications()
  // Reordering is a pointer affordance, and its drag listeners swallow the tap
  // that should follow the link, so phones get a plain list.
  const isMobile = useIsMobile()
  const [editingViewId, setEditingViewId] = useState<string | null>(null)
  const [editingName, setEditingName] = useState('')
  const [hoveredViewId, setHoveredViewId] = useState<string | null>(null)
  const [viewOrder, setViewOrder] = useState<string[]>(getSavedViewsOrder)
  const [isDragActive, setIsDragActive] = useState(false)

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8
      }
    })
  )

  const handleDragStart = (_event: DragStartEvent) => {
    setIsDragActive(true)
  }

  const { data: savedViews, isLoading } = useQuery(
    dashboard20240730Client.getSavedViews,
    undefined,
    {
      select: data => {
        if (!data) {
          return null
        }

        return savedViewsPorts.savedViews.fromServer(data)
      },
      staleTime: SAVED_VIEWS_STALE_TIME
    }
  )

  const orderedViews = useMemo(() => {
    if (!savedViews) return []
    return sortViewsByOrder(savedViews, viewOrder)
  }, [savedViews, viewOrder])

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event

    // Keep drag active briefly to prevent click-through
    setTimeout(() => {
      setIsDragActive(false)
    }, 0)

    if (!over || active.id === over.id) return

    const oldIndex = orderedViews.findIndex(v => v.id === active.id)
    const newIndex = orderedViews.findIndex(v => v.id === over.id)

    if (oldIndex !== -1 && newIndex !== -1) {
      const newOrder = arrayMove(
        orderedViews.map(v => v.id),
        oldIndex,
        newIndex
      )
      setViewOrder(newOrder)
      setSavedViewsOrder(newOrder)
    }
  }

  const putSavedViewMutator = useMutation(dashboard20240730Client.putSavedView)

  const handleEditName = async () => {
    if (!editingViewId || !editingName.trim()) {
      setEditingViewId(null)
      return
    }

    const savedView = savedViews?.find(view => view.id === editingViewId)
    if (!savedView) {
      setEditingViewId(null)
      return
    }

    const resPutSavedView = await putSavedViewMutator.mutateAsync({
      id: savedView.id,
      body: savedViewsPorts.savedView.toServer({
        name: editingName,
        entityType: savedView.entityType,
        activeFilters: savedView.activeFilters,
        sortBy: savedView.sortBy
      })
    })

    if (!resPutSavedView) {
      displayNotification('Failed to update saved view', { type: 'error' })
      return
    }

    displayNotification('Saved view updated', { type: 'success' })
    setEditingViewId(null)
  }

  const savedViewProps = (view: SavedView) => ({
    view,
    isEditing: editingViewId === view.id,
    isHovered: hoveredViewId === view.id,
    isActive: pathname.startsWith(`/saved-view/${view.id}`),
    editingName,
    onEditingNameChange: setEditingName,
    onStartEdit: () => {
      setEditingViewId(view.id)
      setEditingName(view.name)
    },
    onSave: () => void handleEditName(),
    onCancel: () => setEditingViewId(null),
    onMouseEnter: () => setHoveredViewId(view.id),
    onMouseLeave: () => setHoveredViewId(null),
    isCollapsed
  })

  if (!isLoading && !savedViews?.length) {
    return null
  }

  const viewsList = isMobile ? (
    orderedViews.map(view => (
      <EditableSavedView key={view.id} {...savedViewProps(view)} />
    ))
  ) : (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      modifiers={[restrictToVerticalAxis]}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
    >
      <SortableContext
        items={orderedViews}
        strategy={verticalListSortingStrategy}
      >
        {orderedViews.map(view => (
          <SortableSavedView
            key={view.id}
            id={view.id}
            {...savedViewProps(view)}
            isDragActive={isDragActive}
          />
        ))}
      </SortableContext>
    </DndContext>
  )

  return (
    <Box padding="8px 8px" userSelect="none">
      <NavSection
        id="saved-views"
        title="Saved views"
        expandOnMatchingPath={pathname => pathname.startsWith('/saved-view/')}
        isCollapsed={isCollapsed}
      >
        {isLoading ? <NavLinksLoading /> : viewsList}
      </NavSection>
    </Box>
  )
}
